// src/pages/RuleManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  Alert, Snackbar, CircularProgress, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Tooltip, Autocomplete,
  ToggleButtonGroup, ToggleButton, TablePagination, Paper, Tabs, Tab,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Add as AddIcon, Save as SaveIcon,
  Rule as RuleIcon, Search as SearchIcon, PlayArrow as PlayArrowIcon,
  Pause as PauseIcon, AutoAwesome as AutoAwesomeIcon, BarChart as BarChartIcon,
  Delete as DeleteIcon, Edit as EditIcon, Code as CodeIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import Gateway from '../api/gateway';
import RuleEditor from '../components/RuleEditor';

// Common layout
import PageShell from '../components/common/PageShell';
import PagePanel from '../components/common/PagePanel';
import StatCard from '../components/common/StatCard';

// RBAC
import { isAdmin } from '../utils/accessControl';

const RULE_STATUSES = [
  { value: 'active', label: 'Активно', color: 'success', icon: <PlayArrowIcon /> },
  { value: 'inactive', label: 'Неактивно', color: 'error', icon: <PauseIcon /> },
];

const DEFAULT_RULE_TYPE = { label: 'Неизвестный', color: '#757575', icon: '❓' };

const DEFAULT_SCHEMA = { conditionTypes: [], actionTypes: [], operators: [], targetTypes: [] };

// Убираем _uid перед отправкой на бэкенд
const stripUids = (rule) => {
  const clean = { ...rule };
  if (Array.isArray(clean.conditions)) clean.conditions = clean.conditions.map(({ _uid, ...r }) => r);
  if (Array.isArray(clean.actions)) clean.actions = clean.actions.map(({ _uid, ...r }) => r);
  return clean;
};

// ============================================================================
// Список правил
// ============================================================================
function RuleList({ rules, onSelect, selectedBitId, loading, ruleTypes }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!rules || rules.length === 0) {
    return <Alert severity="info" sx={{ m: 1, fontSize: '0.7rem' }}>Нет правил</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {rules.map((rule) => {
          const isSelected = selectedBitId === rule.bit_id;
          const cfg = ruleTypes?.[rule.type] || DEFAULT_RULE_TYPE;
          const status = rule.enabled ? RULE_STATUSES[0] : RULE_STATUSES[1];

          return (
            <Box
              key={rule.bit_id}
              onClick={() => onSelect(rule)}
              sx={{
                p: 0.75,
                cursor: 'pointer',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                borderLeft: 3,
                borderColor: isSelected ? 'primary.main' : 'transparent',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 24, height: 24, borderRadius: '50%', bgcolor: cfg.color,
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', flexShrink: 0,
                  }}
                >
                  {cfg.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'} noWrap sx={{ fontSize: '0.75rem' }}>
                    {rule.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
                    {cfg.label} · {String(rule.bit_id || '').substring(0, 10)}…
                  </Typography>
                </Box>
                <Chip
                  label={status.label}
                  size="small"
                  color={status.color}
                  sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } }}
                />
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ============================================================================
// Статистика
// ============================================================================
function RuleStatistics({ statistics }) {
  if (!statistics) return <Alert severity="info" sx={{ fontSize: '0.7rem' }}>Загрузка…</Alert>;
  const avgMs = Number(statistics.avg_execution_time_ms) || 0;

  return (
    <Grid container spacing={0.5}>
      {[
        { label: 'Всего', val: statistics.total_rules },
        { label: 'Активных', val: statistics.active_rules },
        { label: 'Срабатываний', val: statistics.successful_triggers },
        { label: 'Ср. мс', val: `${avgMs.toFixed(1)}` },
      ].map((stat, idx) => (
        <Grid item xs={6} key={idx}>
          <Paper variant="outlined" sx={{ p: 0.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.55rem' }}>
              {stat.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
              {stat.val ?? 0}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// ============================================================================
// JSON
// ============================================================================
function JsonView({ data }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1, bgcolor: '#1e1e1e', color: '#d4d4d4',
        fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh',
      }}
    >
      <pre style={{ margin: 0, fontSize: '0.65rem', lineHeight: 1.3 }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    </Paper>
  );
}

// ============================================================================
// Главный компонент
// ============================================================================
export default function RuleManager() {
  const auth = useSelector((state) => state.auth);
  const isAuthenticated = auth?.isAuthenticated;
  const canEdit = isAdmin(auth);

  const [rules, setRules] = useState([]);
  const [currentRule, setCurrentRule] = useState(null);
  const [selectedBitId, setSelectedBitId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, totalPages: 0, hasNext: false, hasPrev: false,
  });

  const [statistics, setStatistics] = useState(null);
  const [ruleTypes, setRuleTypes] = useState(null);
  const [schema, setSchema] = useState(DEFAULT_SCHEMA);
  const [newRule, setNewRule] = useState({ name: '', type: '', description: '' });

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const loadRuleTypes = useCallback(async () => {
    try {
      const response = await Gateway.http.imcon.get('/api/rules/types');
      const list = response.data?.rule_types;
      if (Array.isArray(list)) {
        const typesMap = {};
        list.forEach((t) => { typesMap[t.value] = { label: t.label, color: t.color, icon: t.icon }; });
        setRuleTypes(typesMap);
      }
      setSchema({
        conditionTypes: response.data?.condition_types || [],
        actionTypes: response.data?.action_types || [],
        operators: response.data?.operators || [],
        targetTypes: response.data?.target_types || [],
      });
    } catch (error) {
      console.error('loadRuleTypes failed', error);
      showSnackbar('Не удалось загрузить типы правил', 'warning');
    }
  }, []);

  const loadRules = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (filterType) params.type = filterType;
      if (searchTerm) params.search = searchTerm;
      if (filterStatus === 'active') params.enabled_only = 'true';
      if (filterStatus === 'inactive') params.enabled_only = 'false';

      const response = await Gateway.http.imcon.get('/api/rules', { params });
      if (response.data?.status === 'success') {
        setRules(response.data.data || []);
        if (response.data.meta) {
          setPagination((prev) => ({
            ...prev,
            total: response.data.meta.total,
            totalPages: response.data.meta.total_pages,
            hasNext: response.data.meta.has_next,
            hasPrev: response.data.meta.has_prev,
          }));
        }
      } else {
        showSnackbar('Некорректный ответ сервера', 'warning');
      }
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка загрузки: ${reason}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, pagination.page, pagination.limit, filterType, filterStatus, searchTerm]);

  const loadStatistics = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await Gateway.http.imcon.get('/api/rules/stats');
      if (response.data?.statistics) setStatistics(response.data.statistics);
    } catch (error) {
      console.error('loadStatistics failed', error);
    }
  }, [isAuthenticated]);

  useEffect(() => { loadRuleTypes(); }, [loadRuleTypes]);
  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => { loadStatistics(); }, [loadStatistics]);

  const handlePageChange = (_, newPage) => setPagination((p) => ({ ...p, page: newPage + 1 }));
  const handleRowsPerPageChange = (e) =>
    setPagination((p) => ({ ...p, limit: parseInt(e.target.value, 10), page: 1 }));
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleSelectRule = async (rule) => {
    setSelectedBitId(rule.bit_id);
    setLoading(true);
    try {
      const response = await Gateway.http.imcon.get('/api/rules/show', { params: { bit_id: String(rule.bit_id) } });
      if (response.data?.rule) {
        setCurrentRule(response.data.rule);
        setActiveTab(0);
      }
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка загрузки: ${reason}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRuleDraftChange = (nextRule) => {
    if (!canEdit) return;
    setCurrentRule(nextRule);
  };

  const handleSave = async (draft) => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    const ruleToSave = draft || currentRule;
    if (!ruleToSave) return;
    setSaving(true);
    try {
      const payload = stripUids(ruleToSave);
      const response = await Gateway.http.imcon.post('/api/rules', { rule: payload });
      const saved = response.data?.rule;
      if (saved) {
        setCurrentRule(saved);
        setSelectedBitId(saved.bit_id);
      }
      showSnackbar('Правило сохранено', 'success');
      loadRules();
      loadStatistics();
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка сохранения: ${reason}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRule = async () => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    if (!newRule.name.trim()) return;
    try {
      const response = await Gateway.http.imcon.post('/api/rules', {
        rule: {
          ...newRule, enabled: true, confidence: 0.5, source: 'manual',
          event_types: [], conditions: [], actions: [], priority: 50,
        },
      });
      const created = response.data?.rule;
      if (created) {
        setCurrentRule(created);
        setSelectedBitId(created.bit_id);
        setCreateDialogOpen(false);
        setNewRule({ name: '', type: '', description: '' });
        showSnackbar('Правило создано', 'success');
        loadRules();
        loadRuleTypes();
      } else {
        showSnackbar('Сервер не вернул правило', 'warning');
      }
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка создания: ${reason}`, 'error');
    }
  };

  const handleDeleteRule = async (ruleFromEditor) => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    const r = ruleFromEditor || currentRule;
    if (!r) return;
    if (!window.confirm(`Удалить правило "${r.name}"?`)) return;
    showSnackbar('Эндпоинт удаления не реализован', 'warning');
  };

  const handleUpdateStatus = async (status) => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentRule) return;
    try {
      const response = await Gateway.http.imcon.put(
        '/api/rules/status', { status }, { params: { bit_id: String(currentRule.bit_id) } }
      );
      if (response.data?.rule) {
        setCurrentRule(response.data.rule);
        showSnackbar('Статус обновлён', 'success');
        loadRules();
        loadStatistics();
      }
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка статуса: ${reason}`, 'error');
    }
  };

  const handleGenerateFromForecast = async () => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    const forecastId = window.prompt('ID прогноза:');
    if (!forecastId) return;
    try {
      await Gateway.http.imcon.post('/api/rules/generate_from_forecast', { forecast_id: forecastId, context: {} });
      showSnackbar('Генерация запущена', 'success');
      loadRules();
    } catch (error) {
      const reason = error?.response?.data?.reason || error.message;
      showSnackbar(`Ошибка генерации: ${reason}`, 'error');
    }
  };

  if (!isAuthenticated) {
    return (
      <PageShell>
        <PagePanel>
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">Требуется авторизация</Alert>
          </Box>
        </PagePanel>
      </PageShell>
    );
  }

  // ==========================================================================
  // Шапка
  // ==========================================================================
  const headerPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 1, px: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <RuleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.05rem' }}>
                Управление правилами СИМ
              </Typography>
              {!canEdit && (
                <Chip
                  icon={<LockIcon sx={{ fontSize: 12 }} />}
                  label="Только просмотр"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.6rem' } }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Редактирование логики, условий и действий
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <StatCard compact title="Всего" value={statistics?.total_rules ?? '—'} color="primary" icon={<RuleIcon fontSize="small" />} />
            <StatCard compact title="Активных" value={statistics?.active_rules ?? '—'} color="success" icon={<PlayArrowIcon fontSize="small" />} />
            <StatCard compact title="Срабатываний" value={statistics?.successful_triggers ?? '—'} color="warning" icon={<BarChartIcon fontSize="small" />} />
          </Stack>
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // Панель действий
  // ==========================================================================
  const actionsPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 0.75, px: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
            onClick={() => { loadRuleTypes(); loadRules(); loadStatistics(); }}
            disabled={loading}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            Обновить
          </Button>

          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<AutoAwesomeIcon fontSize="small" />}
              onClick={handleGenerateFromForecast}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Из прогноза
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          {canEdit && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Создать правило
            </Button>
          )}
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // JSX
  // ==========================================================================
  return (
    <PageShell>
      {headerPanel}
      {actionsPanel}

      <Box
        sx={{
          flex: 1, minHeight: 0,
          display: 'flex', gap: 0.75, overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Левая колонка */}
        <PagePanel
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1, pt: 1, pb: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            <Stack spacing={0.5}>
              <Autocomplete
                options={
                  ruleTypes
                    ? Object.entries(ruleTypes).map(([v, c]) => ({ value: v, label: c.label }))
                    : []
                }
                getOptionLabel={(o) => o.label || ''}
                renderInput={(p) => (
                  <TextField {...p} label="Тип" size="small" sx={{ '& input': { fontSize: '0.75rem' } }} />
                )}
                size="small"
                onChange={(e, v) => handleFilterChange(setFilterType)(v ? v.value : '')}
                value={
                  filterType && ruleTypes
                    ? { value: filterType, label: ruleTypes[filterType]?.label || filterType }
                    : null
                }
                isOptionEqualToValue={(a, b) => a.value === b.value}
                noOptionsText="Типы не найдены"
              />
              <ToggleButtonGroup
                value={filterStatus}
                exclusive
                onChange={(e, v) => handleFilterChange(setFilterStatus)(v || '')}
                size="small"
                fullWidth
              >
                <ToggleButton value="active" sx={{ fontSize: '0.65rem', py: 0.25 }}>
                  <PlayArrowIcon sx={{ fontSize: 12, mr: 0.25 }} /> Активные
                </ToggleButton>
                <ToggleButton value="inactive" sx={{ fontSize: '0.65rem', py: 0.25 }}>
                  <PauseIcon sx={{ fontSize: 12, mr: 0.25 }} /> Неактивные
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                placeholder="Поиск..."
                value={searchTerm}
                onChange={(e) => handleFilterChange(setSearchTerm)(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5 }} />,
                  sx: { fontSize: '0.75rem' },
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ px: 1, py: 0.75 }}>
            <RuleStatistics statistics={statistics} />
          </Box>

          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <RuleList
                rules={rules}
                onSelect={handleSelectRule}
                selectedBitId={selectedBitId}
                loading={loading}
                ruleTypes={ruleTypes}
              />
            </Box>
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.max(pagination.page - 1, 0)}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[20, 50, 100]}
              labelRowsPerPage=""
              nextIconButtonProps={{ disabled: !pagination.hasNext }}
              backIconButtonProps={{ disabled: !pagination.hasPrev }}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                minHeight: 32,
                '& .MuiTablePagination-toolbar': { minHeight: 32 },
                '& .MuiTablePagination-selectLabel': { display: 'none' },
              }}
            />
          </Box>
        </PagePanel>

        {/* Правая колонка */}
        <PagePanel sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, p: 1 }}>
            {currentRule ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="h6" noWrap sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
                    {currentRule.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={ruleTypes?.[currentRule.type]?.label || currentRule.type}
                      color="primary"
                      size="small"
                      sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                    />
                    <Chip
                      label={`Приоритет: ${currentRule.priority}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                    />
                    <Chip
                      label={`Увер.: ${Math.round((currentRule.confidence || 0) * 100)}%`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                    />
                    <Chip
                      label={currentRule.source}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                    />
                  </Stack>
                </Box>

                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{
                    minHeight: 28, mb: 1,
                    borderBottom: 1, borderColor: 'divider',
                    '& .MuiTab-root': {
                      minHeight: 28, fontSize: '0.7rem', textTransform: 'none',
                      py: 0.25, px: 1, minWidth: 'auto',
                    },
                  }}
                >
                  <Tab icon={<EditIcon fontSize="small" />} iconPosition="start" label="Редактор" />
                  <Tab icon={<CodeIcon fontSize="small" />} iconPosition="start" label="JSON" />
                  <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Статистика" />
                </Tabs>

                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {activeTab === 0 && (
                    <RuleEditor
                      rule={currentRule}
                      onChange={handleRuleDraftChange}
                      onSave={handleSave}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDeleteRule}
                      ruleTypes={ruleTypes}
                      schema={schema}
                      readOnly={!canEdit}
                    />
                  )}
                  {activeTab === 1 && <JsonView data={currentRule} />}
                  {activeTab === 2 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                        Статистика выполнения
                      </Typography>
                      <Grid container spacing={0.75}>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Срабатываний
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {currentRule.statistics?.triggers_count || 0}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Успешных
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {currentRule.statistics?.success_count || 0}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Ошибок
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {currentRule.statistics?.failure_count || 0}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Ср. время, мс
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(currentRule.statistics?.avg_execution_time_ms || 0).toFixed(2)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}>
                <RuleIcon sx={{ fontSize: 56, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  Правило не выбрано
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Выберите правило из списка слева
                </Typography>
              </Box>
            )}
          </Box>
        </PagePanel>
      </Box>

      {/* Диалог создания — только canEdit */}
      {canEdit && (
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1rem' }}>Новое правило</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Название"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                fullWidth
                size="small"
                required
                sx={{ '& input': { fontSize: '0.8rem' } }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Тип</InputLabel>
                <Select
                  value={newRule.type}
                  label="Тип"
                  onChange={(e) => setNewRule({ ...newRule, type: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {ruleTypes &&
                    Object.entries(ruleTypes).map(([v, c]) => (
                      <MenuItem key={v} value={v} sx={{ fontSize: '0.8rem' }}>
                        {c.icon} {c.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label="Описание"
                multiline
                rows={3}
                value={newRule.description}
                onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                fullWidth
                size="small"
                sx={{ '& textarea': { fontSize: '0.8rem' } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>
              Отмена
            </Button>
            <Button
              onClick={handleCreateRule}
              variant="contained"
              size="small"
              disabled={!newRule.name.trim()}
              sx={{ fontSize: '0.7rem' }}
            >
              Создать
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
}